import { useState, useEffect, CSSProperties } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import {
  Box,
  Grid,
  Typography,
  Modal,
  CircularProgress,
  Chip,
} from '@mui/material';
import { saveAs } from 'file-saver';
import { StyledButton } from '@/components/styled/button';
import html2canvas from 'html2canvas';
import ShapeTooltip from './components/ShapeTooltip';
import { generateAnswersTable } from './components/ResultsBreakdownTable';
import useIsMobile from '@/lib/useIsMobile';
import { Header } from '@/components/header';

const AGGREGATED_SUBMISSION = `AGGREGATED_SUBMISSION`;

const findMinMax = (
  categories: { name: string; maxScore: number }[],
  scores: number[],
  findMin: boolean
) => {
  let minCategory: { name: string; score: number } = { name: '', score: 4 };
  let maxCategory: { name: string; score: number } = { name: '', score: -1 };
  for (let i = 0; i < scores.length; i++) {
    if (scores[i] > maxCategory.score) {
      maxCategory = { name: categories[i].name, score: scores[i] };
    } else if (scores[i] < minCategory.score) {
      minCategory = { name: categories[i].name, score: scores[i] };
    }
  }

  if (findMin) {
    return minCategory;
  } else {
    return maxCategory;
  }
};

const ClientProductShape = ({
  scores,
  design,
  jsonData,
  questions,
  userAnswers,
}: any) => {
  ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    {
      id: 'custom_canvas_background_color',
      beforeDraw: (chart) => {
        const ctx = chart.canvas.getContext('2d');
        ctx!.save();
        ctx!.globalCompositeOperation = 'destination-over';
        ctx!.fillStyle = 'rgba(32, 32, 32, 1)';
        ctx!.fillRect(0, 0, chart.width, chart.height);
        ctx!.restore();
      },
    }
  );
  const isMobile = useIsMobile();

  const [saving, setSaving] = useState(false);
  const firstName =
    userAnswers
      .find((a: any) => a.questionId === 'YVB4B7')
      ?.data.replace(/\"/g, '') ?? '';
  const lastName =
    userAnswers
      .find((a: any) => a.questionId === 'Q1zgd7')
      ?.data.replace(/\"/g, '') ?? '';
  const teamName =
    userAnswers
      .find((a: any) => a.questionId === 'K7yoMV')
      ?.data.replace(/\"/g, '') ?? '';

  useEffect(() => {
    if (saving) {
      saveResults();
      stopSaving();
    }
  }, [saving]);

  const saveResults = async () => {
    const endPage = document.getElementById('save-div');
    const canvas = await html2canvas(endPage!);
    canvas.toBlob(function (blob) {
      saveAs(blob!, 'client-product-shape-chart.png');
    });
  };

  // used to show saving modal for a second, without it modal just flashes in and out
  const stopSaving = async () => {
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    await sleep(1000);
    setSaving(false);
  };

  const saveChart = () => {
    //save to png
    const canvasSave = document.getElementById('client-product-shape-chart');
    // @ts-ignore: Object is possibly 'null'.
    canvasSave.toBlob(function (blob) {
      saveAs(blob, 'client-product-shape-chart.png');
    });
  };

  // console.log(chart.config.options) to see this object
  const chartData = {
    labels: jsonData.chartCategories,
    datasets: [
      {
        label: 'Shape',
        data: scores,
        backgroundColor: 'rgba(134, 188, 37, 0.2)',
        borderColor: 'rgba(134, 188, 37, 1)',
        color: 'rgba(255, 255, 255, 0.75)',
        borderWidth: 1,
      },
    ],
  };

  // console.log(chart.config.options) to see this object
  const chartOptions: {
    plugins: {
      legend: {
        display: boolean;
        position: any;
      };
    };
    scales: {
      r: {
        min: number;
        max: number;
        ticks: {
          stepSize: number;
          callback: any;
        };
        grid: {
          color: string;
          lineWidth: number;
        };
        pointLabels: any;
      };
    };
  } = {
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
    },
    scales: {
      r: {
        min: 0,
        max: 3,
        ticks: {
          stepSize: 1,
          callback: function (index: number): string {
            switch (index) {
              case 1:
                return 'Proficient';
              case 2:
                return 'Skilled';
              case 3:
                return 'Expert';
              default:
                return '';
            }
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.75)',
          lineWidth: 2,
        },
        pointLabels: {
          /* Adjust data label font size according to chart size */
          font: {
            size: window.innerWidth >= 600 ? 14 : 8,
          },
          color: 'rgba(255, 255, 255, 1)',
        },
      },
    },
  };

  const modalStyling: CSSProperties = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    border: `2px solid ${design.colors.question}`,
    borderRadius: '8px',
    backgroundColor: 'design.colors.background',
    color: design.colors.button,
    padding: '40px',
  };

  return (
    <Box data-testid='client-product-shape-wrapper-box'>
      <Modal
        open={saving}
        aria-labelledby='saving-modal'
        aria-describedby='appears when results are being saved'
      >
        <Box sx={modalStyling}>
          <CircularProgress color='inherit' />
        </Box>
      </Modal>
      <div id='save-div' style={{ backgroundColor: design.colors.background }}>
        <Header design={design} />
        <Grid
          container
          justifyContent='center'
          alignItems='center'
          sx={{
            padding: [
              '15vh 5% 0 5%',
              '15vh 5% 0 5%',
              '15vh 5% 0 5%',
              '15vh 5% 0 5%',
              '15vh 10% 0 10%',
            ],
          }}
        >
          <Grid item xs={12} sx={{ my: 4 }}>
            <Typography
              variant='h4'
              align='center'
              color={design.colors.question}
              marginTop='20px'
            >
              {/* PM Name's or Team Name's Product Shape Overview */}
              {firstName !== AGGREGATED_SUBMISSION &&
              lastName !== AGGREGATED_SUBMISSION
                ? `${firstName} ${lastName}`
                : `${teamName}`}
              {`'s Product Shape Overview`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              align='center'
              color={design.colors.question}
              sx={{
                margin: '5vh 0',
                whiteSpace: 'pre-line',
                lineHeight: 2,
              }}
            >
              Thank you for taking the time to complete the survey! Your results
              have been used to create the product shape below.
              <br></br>The Product Shape is a tool for product managers to
              evaluate their own talents and expertise across various areas.
            </Typography>
          </Grid>
          {!saving && !isMobile && (
            <StyledButton
              background={design.colors.button}
              $buttonColor={design.colors.buttonText}
              highlight={design.colors.buttonActive}
              className={'save-button'}
              onClick={() => setSaving(true)}
              style={{
                fontWeight: 'bold',
                width: '300px',
                marginBottom: '4rem',
                borderRadius: 16,
              }}
              disableRipple
            >
              Download Survey results
            </StyledButton>
          )}
          {/* Product shape, areas of growth and tooltip: */}
          <Grid item xs={12} sx={{ marginBottom: '4rem' }}>
            <Box display='flex' sx={{ color: 'white', padding: '20px' }}>
              <Typography variant='h4' fontSize='2rem' align='left'>
                Product Shape
                {!saving && <ShapeTooltip />}
              </Typography>
            </Box>

            <Grid container>
              <Grid item xs={12} md={6}>
                <Box>
                  <Radar
                    id='client-product-shape-chart'
                    data={chartData}
                    options={chartOptions}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container rowSpacing={4}>
                  <Grid item xs={6} md={12}>
                    <Box marginLeft={window.innerWidth >= 900 ? '4rem' : ''}>
                      <Typography
                        variant='body1'
                        color='gray'
                        fontWeight='bold'
                        whiteSpace='pre'
                      >
                        {window.innerWidth >= 600
                          ? `Current Strong Area`
                          : `Current\nStrong Area`}
                      </Typography>
                      <Typography
                        variant='h5'
                        color='white'
                        fontWeight='bold'
                        style={{ marginTop: '0.5rem' }}
                      >
                        {findMinMax(jsonData.categories, scores, false).name}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={12}>
                    <Box marginLeft={window.innerWidth >= 900 ? '4rem' : ''}>
                      <Typography
                        variant='body1'
                        color='gray'
                        fontWeight='bold'
                        whiteSpace='pre'
                      >
                        {window.innerWidth >= 600
                          ? `Current Growth Area`
                          : `Current\nGrowth Area`}
                      </Typography>
                      <Typography
                        variant='h5'
                        color='white'
                        fontWeight='bold'
                        style={{ marginTop: '0.5rem' }}
                      >
                        {findMinMax(jsonData.categories, scores, true).name}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Shape breakdown table: */}
          <Grid item xs={12}>
            <Typography
              variant='h4'
              align='left'
              sx={{
                color: 'white',
                padding: '20px 10px 20px 20px',
                marginBottom: '20px',
              }}
            >
              Product Shape Breakdown
            </Typography>

            <Box
              display='flex'
              alignItems='center'
              justifyContent={window.innerWidth >= 900 ? 'left' : 'center'}
              marginBottom='20px'
              flexWrap='wrap'
            >
              <Typography
                variant='body1'
                fontWeight='bold'
                align='center'
                sx={{
                  color: '#868686',
                  marginBottom: '20px',
                  margin: '0 8px 0 0', // Add margin to create space around Legend Typography
                  minWidth: '60px',
                }}
              >
                Legend
              </Typography>
              <Box display='flex' flexWrap='nowrap'>
                <Chip
                  variant='outlined'
                  label='PROFICIENT'
                  style={{
                    backgroundColor: '#D4D4D4',
                    color: '##4F4D4D',
                    border: '1px solid #4F4D4D',
                    margin: '0 4px 0 0', // Add margin to create space btwn words and chips
                    // fontSize: window.innerWidth >= 600 ? '14px' : '10px',
                  }}
                />
                <Chip
                  variant='outlined'
                  label='SKILLED'
                  style={{
                    backgroundColor: '#A6C9FF',
                    color: '#29508C',
                    border: '1px solid #4F4D4D',
                    margin: '0 4px 0 0', // Add margin to create space btwn words and chips
                    // fontSize: window.innerWidth >= 600 ? '14px' : '10px',
                  }}
                />
                <Chip
                  variant='outlined'
                  label='EXPERT'
                  style={{
                    backgroundColor: '#5187DB',
                    color: '#001739',
                    border: '1px solid #001739',
                    margin: '0 4px 0 0', // Add margin to create space btwn words and chips
                    // fontSize: window.innerWidth >= 600 ? '14px' : '10px',
                  }}
                />
              </Box>
            </Box>
            {/* Add space btween table and bottom of page */}
            <Box marginBottom='50px'>
              {/* Calls results table function to create table removed userAnswers and questions because only really need scores */}
              {generateAnswersTable(
                [
                  'Product Design &\nExecution',
                  'User &\nExperience',
                  'Business &\nMarketing',
                  'Leadership &\nManagement',
                ],
                scores,
                window.innerWidth <= 900
              )}
            </Box>
          </Grid>
        </Grid>
      </div>
    </Box>
  );
};

export default ClientProductShape;
